/*
 * File: request.ts
 * File Created: 2024-04-11 05:22:56
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2025-12-09 10:12:15
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { IServer } from "@root/components/interfaces";
import { IQuery, IQueryDefault, ObjectHelper, Retrier } from "@root/core";
import { debounce } from "@root/core/decorators/debounce";
import { queryReload } from "@root/core/decorators/query-reload";
import { CreateElement } from "vue";
import { Vue, Component, Prop, Watch, Emit } from "vue-property-decorator";

@Component
export default class Request extends Vue {
    /// public props ///////////////////////
    /// The URI for this request.
    @Prop({ type: Object, required: false })
    server: IServer | undefined;

    // hold the data / loading / error
    @Prop({ type: Object, default: () => IQueryDefault() })
    query!: IQuery;

    // reload interval
    @Prop({ type: Number, required: false })
    interval!: number;

    /// wait for login to send the request
    @Prop({ type: Boolean, default: false })
    waitForLogin!: boolean;

    /// will the error remain silent / no report
    @Prop({ type: Boolean, default: false })
    silent!: boolean;

    @Emit("reload")
    doReload() {
        return this.query;
    }

    /// public function
    private _reloadTimeout;
    @debounce(10)
    @queryReload()
    async reload() {
        if (this._reloadTimeout) {
            clearTimeout(this._reloadTimeout);
            this._reloadTimeout = undefined;
        }

        if (this.waitForLogin) {
            await (this.$server as any).waitForLogin();
        }

        const server = this.server?.server ?? this.$server;
        const path = this.server?.path;
        const params = this.server?.params;
        if (path == null) return;

        server
            .useQuery(this.query, server.R, path as any, params, { silent: this.silent })
            .then(() => this.doReload())
            .finally(() => {
                if (!this.interval) return;
                this._reloadTimeout = setTimeout(() => {
                    this.reload();
                }, this.interval);
            });
    }

    /// private helpers
    /// when server changed with param id, do reload.
    @Watch("server", { immediate: true })
    onServerUpdated(value: IServer | undefined, oldValue: IServer | undefined) {
        /// if not changing, skip update.
        if (ObjectHelper.equals(value, oldValue)) return;

        /// if no value, skip.
        if (value == undefined) return;
        const params = value.params;
        // /// if no where criteria, skip fetching.
        // if (params?.where == undefined) return;

        this.$nextTick(() => this.reload());
    }

    render(h: CreateElement) {
        const slot = this.$scopedSlots["@query"];
        if (slot) {
            return slot({ query: this.query });
        }
        return null;
    }
}
